import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth, api } from '../../context/AuthContext';
import { ArrowLeft, Edit, MessageCircle, Check, Send } from 'lucide-react';

export default function ProductDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [chats, setChats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeChat, setActiveChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [markingSold, setMarkingSold] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Wait for user to be loaded
    const timer = setTimeout(() => {
      if (!user) {
        navigate('/login');
        return;
      }
      // Allow all users (BUYER, SELLER, ADMIN) to view product details
      loadProduct();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user, id]);

const API_URL = '/api';

  const loadProduct = async () => {
    try {
      // Loading product:
      const res = await fetch(`${API_URL}/products/${id}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!res.ok) {
        throw new Error('Producto no encontrado');
      }
      
      const data = await res.json();
      // Product loaded:
      
      if (data.seller_id !== user?.id && user?.role !== 'ADMIN') {
        navigate('/seller/products');
        return;
      }
      
      setProduct(data);
      await loadChats(data.id);
    } catch (err) {
      console.error('Error loading product:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const loadChats = async (productId) => {
    try {
      // Loading chats for product:
      const res = await fetch(`${API_URL}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!res.ok) throw new Error('Error cargando mensajes');
      
      const data = await res.json();
      // Messages loaded:
      
      // Filter messages for this product
      const productMessages = data.filter(m => m.product_id === productId);
      
      // Group by sender (buyer)
      const chatMap = {};
      productMessages.forEach(m => {
        if (!chatMap[m.sender_id]) {
          chatMap[m.sender_id] = {
            user_id: m.sender_id,
            user_name: m.sender?.name || 'Usuario',
            user_avatar: m.sender?.avatar,
            messages: [],
            lastMessage: m
          };
        }
        chatMap[m.sender_id].messages.push(m);
      });
      
      // Also include messages sent TO this seller about this product
      const incomingMessages = data.filter(m => 
        m.receiver_id === user?.id && m.product_id === productId
      );
      
      incomingMessages.forEach(m => {
        if (!chatMap[m.sender_id]) {
          chatMap[m.sender_id] = {
            user_id: m.sender_id,
            user_name: m.sender?.name || 'Usuario',
            user_avatar: m.sender?.avatar,
            messages: [],
            lastMessage: m
          };
        }
        if (!chatMap[m.sender_id].messages.find(msg => msg.id === m.id)) {
          chatMap[m.sender_id].messages.push(m);
        }
      });
      
      const chatList = Object.values(chatMap).sort((a, b) => 
        new Date(b.lastMessage?.created_at || 0) - new Date(a.lastMessage?.created_at || 0)
      );
      
      // Chats count:
      setChats(chatList);
    } catch (err) {
      console.error('Error loading chats:', err);
    }
  };

  const selectChat = (chat) => {
    setActiveChat(chat);
    const sorted = [...chat.messages].sort((a, b) => 
      new Date(a.created_at) - new Date(b.created_at)
    );
    setMessages(sorted);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !activeChat) return;
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          receiver_id: activeChat.user_id,
          product_id: product.id,
          content: newMessage
        })
      });
      
      if (!res.ok) throw new Error('Error enviando mensaje');
      
      const data = await res.json();
      setMessages([...messages, data]);
      setNewMessage('');
    } catch (err) {
      alert('Error al enviar mensaje');
    }
  };

  const markAsSold = async () => {
    if (!activeChat) return;
    if (!confirm(`¿Marcar como vendido a ${activeChat.user_name}?`)) return;
    setMarkingSold(true);
    
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/products/${product.id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: 'SOLD' })
      });
      
      if (!res.ok) throw new Error('Error');
      
      alert(`Producto vendido a ${activeChat.user_name}. El comprador puede calificarte.`);
      loadProduct();
    } catch (err) {
      alert('Error al marcar como vendido');
    } finally {
      setMarkingSold(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-500">Cargando...</p>
        </div>
      </div>
    );
  }

  if (error || !product) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-500 mb-4">{error || 'Producto no encontrado'}</p>
          <button 
            onClick={() => navigate('/seller/products')}
            className="bg-primary text-white px-6 py-2 rounded-full"
          >
            Volver a Mis Productos
          </button>
        </div>
      </div>
    );
  }

  const productImage = product.images?.[0]?.url || 'https://via.placeholder.com/400x300';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b py-4">
        <div className="max-w-6xl mx-auto px-4 flex items-center gap-4">
          <button onClick={() => navigate('/seller/products')} className="p-2 hover:bg-gray-100 rounded-full">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <h1 className="text-xl font-bold">Gestionar Producto</h1>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid md:grid-cols-3 gap-6">
          {/* Product Info */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <img 
                src={productImage}
                alt={product.title}
                className="w-full aspect-video object-cover rounded-lg mb-4"
              />
              <h2 className="font-bold text-lg">{product.title}</h2>
              <p className="text-2xl font-bold text-primary mt-2">
                ₡{Number(product.price).toLocaleString('es-CR')}
              </p>
              <div className="flex gap-2 mt-3">
                <span className={`px-3 py-1 rounded-full text-sm ${
                  product.status === 'ACTIVE' ? 'bg-green-100 text-green-700' :
                  product.status === 'SOLD' ? 'bg-blue-100 text-blue-700' :
                  'bg-gray-100 text-gray-700'
                }`}>
                  {product.status}
                </span>
                <span className="text-sm text-gray-500">{product.condition}</span>
              </div>
              
              <Link 
                to={`/seller/products/${product.id}/edit`}
                className="mt-4 flex items-center justify-center gap-2 bg-primary text-white py-2 rounded-full hover:bg-primary-dark transition"
              >
                <Edit className="w-4 h-4" />
                Editar Producto
              </Link>
            </div>
          </div>

          {/* Chats Section */}
          <div className="md:col-span-2">
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
              <div className="p-4 border-b">
                <h3 className="font-bold flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Mensajes de Compradores
                </h3>
                {product.status === 'SOLD' && (
                  <p className="text-sm text-green-600 mt-1 font-medium">
                    ✓ Producto vendido - Los compradores pueden calificar
                  </p>
                )}
              </div>

              {chats.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <MessageCircle className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No hay mensajes de compradores aún</p>
                  <p className="text-sm mt-2">Cuando un comprador te escriba, aparecerá aquí</p>
                </div>
              ) : (
                <div className="flex h-[500px]">
                  {/* Chat List */}
                  <div className="w-64 border-r overflow-y-auto">
                    {chats.map((chat, index) => (
                      <button
                        key={chat.user_id + '-' + index}
                        onClick={() => selectChat(chat)}
                        className={`w-full p-4 flex items-center gap-3 hover:bg-gray-50 border-b transition ${
                          activeChat?.user_id === chat.user_id ? 'bg-yellow-50' : ''
                        }`}
                      >
                        <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center font-bold text-black">
                          {chat.user_name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 text-left overflow-hidden">
                          <p className="font-medium truncate">{chat.user_name}</p>
                          <p className="text-xs text-gray-500 truncate">
                            {chat.lastMessage?.content?.substring(0, 25) || 'Sin mensajes'}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>

                  {/* Chat Messages */}
                  <div className="flex-1 flex flex-col">
                    {activeChat ? (
                      <>
                        <div className="p-4 border-b flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center font-bold text-black">
                              {activeChat.user_name?.charAt(0).toUpperCase() || 'U'}
                            </div>
                            <div>
                              <p className="font-bold">{activeChat.user_name}</p>
                              <span className="text-xs text-gray-500">Comprador</span>
                            </div>
                          </div>
                          
                          {product.status !== 'SOLD' && (
                            <button
                              onClick={markAsSold}
                              disabled={markingSold}
                              className="bg-green-500 text-white px-4 py-2 rounded-full text-sm font-medium hover:bg-green-600 transition flex items-center gap-2"
                            >
                              <Check className="w-4 h-4" />
                              {markingSold ? 'Guardando...' : 'Marcar vendido'}
                            </button>
                          )}
                        </div>

                        {/* Messages */}
                        <div className="flex-1 overflow-y-auto p-4 space-y-3">
                          {messages.map((msg, idx) => (
                            <div 
                              key={msg.id || idx}
                              className={`flex ${msg.sender_id === user?.id ? 'justify-end' : 'justify-start'}`}
                            >
                              <div className={`max-w-[70%] p-3 rounded-lg ${
                                msg.sender_id === user?.id 
                                  ? 'bg-primary text-white' 
                                  : 'bg-gray-100 text-black'
                              }`}>
                                <p>{msg.content}</p>
                                <p className="text-xs opacity-70 mt-1">
                                  {new Date(msg.created_at).toLocaleString('es-CR', { 
                                    dateStyle: 'short', 
                                    timeStyle: 'short' 
                                  })}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Input */}
                        <div className="p-4 border-t flex gap-2">
                          <input
                            type="text"
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && sendMessage()}
                            placeholder="Escribe un mensaje..."
                            className="flex-1 border rounded-full px-4 py-2 focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                          <button 
                            onClick={sendMessage}
                            className="bg-primary text-white p-2 rounded-full hover:bg-primary-dark transition"
                          >
                            <Send className="w-5 h-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="flex-1 flex items-center justify-center text-gray-500">
                        <p>Selecciona un chat para ver los mensajes</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}